import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, input } from "@angular/core";
import { ActivatedRoute, ParamMap, Params, Router } from "@angular/router";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ProcessVersionService, ProcessVersionWithAvailability } from "common/version/process-version.service";
import { ProcessInterfacesFilterService } from "management/process-interfaces/process-interfaces-filter.service";
import { ProcessInterfacesParams } from "management/process-interfaces/process-interfaces.component";
import { NgClass } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { TrimDirective } from "common/trim.directive";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { DebouncedChangeDirective } from "common/debounced-change.directive";

export interface DropdownEntry {
	ident: string;
	translate: string;
}

@Component({
	selector: "stages-process-interfaces-filter",
	templateUrl: "./process-interfaces-filter.component.html",
	styleUrls: ["./process-interfaces-filter.component.scss"],
	imports: [NgClass, TranslateModule, TrimDirective, ReactiveFormsModule, FormsModule, DebouncedChangeDirective],
})
export class ProcessInterfacesFilterComponent implements OnInit, OnDestroy, OnChanges {
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private processVersionService = inject(ProcessVersionService);
	private processInterfacesFilterService = inject(ProcessInterfacesFilterService);

	private destroy$ = new Subject<boolean>();

	_dropdownContent: DropdownEntry[] = [];
	_processVersions?: ProcessVersionWithAvailability[];
	_currentWorkspaceId!: string;
	_queryParams?: ParamMap;

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input() dropdownContent: DropdownEntry[] = [];

	readonly showSearch = input<boolean>(false);

	readonly placeholderText = input<string>("management.process.interfaces.filter.search");

	readonly standaloneStyle = input<boolean>(true);

	workingVersionSelected = false;
	dropdownValue?: string;
	searchTermValue: string = "";

	ngOnInit(): void {
		combineLatest([this.route.paramMap, this.route.queryParamMap])
			.pipe(takeUntil(this.destroy$))
			// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Remove this comment and fix warnings!
			.subscribe(async ([urlParamMap, urlQueryParamMap]) => {
				this._currentWorkspaceId = urlParamMap.get(ProcessInterfacesParams.WORKSPACEID)!;
				this._queryParams = urlQueryParamMap;

				if (!this._processVersions) {
					this._processVersions = await this.processVersionService.getProcessVersions(this._currentWorkspaceId, true);
				}

				if (this._processVersions) {
					const specificProcessVersion = urlQueryParamMap.has(ProcessInterfacesParams.PVFORINTERFACES)
						? urlQueryParamMap.get(ProcessInterfacesParams.PVFORINTERFACES)
						: urlParamMap.get(ProcessInterfacesParams.PV)!;

					this.workingVersionSelected =
						this._processVersions.find(
							(version) => specificProcessVersion === ProcessInterfacesParams.WORKINGVERSION && version.workingVersion,
						) !== undefined;
				}

				this.processInterfacesFilterService.setFilterToolbarVisible(this.isFilterToolbarVisible());
			});

		this.processInterfacesFilterService.searchTerm$.pipe(takeUntil(this.destroy$)).subscribe((searchTerm) => {
			if (searchTerm !== this.searchTermValue) {
				this.searchTermValue = searchTerm;
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	async setDropdownValue(queryParams?: ParamMap): Promise<void> {
		if (this.dropdownContent.length > 0) {
			const oldDropdownValue = this.dropdownValue;
			if (queryParams?.has(ProcessInterfacesParams.TYPE)) {
				const typeName = queryParams.get(ProcessInterfacesParams.TYPE)!;
				this.dropdownValue = this.dropdownContent.find((entry) => entry.ident === typeName)?.ident;
			}
			if (this.dropdownValue === undefined) {
				this.dropdownValue = this.dropdownContent[0].ident;
			}
			if (oldDropdownValue !== this.dropdownValue) {
				await this.onFilterTypeChange();
			}
		}
	}

	isFilterToolbarVisible(): boolean {
		return this.dropdownContent.length > 0 || this.showSearch();
	}

	onSearchTermChange(): void {
		this.processInterfacesFilterService.setSearchTerm(this.searchTermValue);
	}

	async onFilterTypeChange(): Promise<void> {
		this.processInterfacesFilterService.setFilterType(this.dropdownValue!);
		const queryFilterType = this.dropdownValue!.length <= 0 ? null : this.dropdownValue;
		await this.internalRouterNavigateWithQueryParams({ type: queryFilterType });
	}

	async internalRouterNavigateWithQueryParams(params: Params): Promise<void> {
		await this.router.navigate([], {
			queryParamsHandling: ProcessInterfacesParams.MERGE,
			queryParams: params,
			replaceUrl: true,
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Remove this comment and fix warnings!
	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		const dropdownContent = changes.dropdownContent;
		if (
			dropdownContent &&
			this._dropdownContent.toString() !== dropdownContent.currentValue.toString() &&
			dropdownContent.currentValue.length > 0
		) {
			this._dropdownContent = changes.dropdownContent.currentValue;
			await this.setDropdownValue(this._queryParams);
		}
	}
}
